import { Component, EventEmitter, Input, Output } from '@angular/core';
import type { AliasedIcon } from 'components/icon-registry';
import {
	LoButton,
	LoCard,
	LoNotice,
	LoStatus,
	LoTooltip
} from 'lo-design-system';
import { ChatChannelState, ChatPlatform } from './chat-channels.service';

/**
 * One platform's card, fully resolved by the block that owns it.
 *
 * Every string here is precomputed rather than derived in the template: Angular
 * re-evaluates a template getter on every change-detection pass, and this page
 * runs three other integration blocks beside it.
 */
export interface ChatChannelCardModel {
	platform: ChatPlatform;
	/** Alias in `aliasedIcons`. */
	icon: AliasedIcon;
	name: string;
	/** What Ana does on this platform, one line. */
	description: string;
	state: ChatChannelState;
	statusLabel: string;
	/** The workspace, as the card names it. Null before anything is bound. */
	workspaceLabel: string | null;
	/** The platform's own identifier, shown small beside the workspace. */
	workspaceReference: string | null;
	/** Why the connection stopped. Set only in the `broken` state. */
	problem: string | null;
	/** Slack only — Teams has no round trip to start (see the block). */
	canConnect: boolean;
	connectLabel: string;
	canRevoke: boolean;
	/** Teams only: the card offers instructions instead of a Connect button. */
	canShowInstructions: boolean;
	instructionsLabel: string;
	/** Whether the lower half of the card has anything in it. Precomputed so the
	 *  header's bottom radius is a class binding rather than a `:has()` rule. */
	hasDetails: boolean;
}

@Component({
	standalone: true,
	selector: 'chat-channel-card',
	imports: [LoButton, LoCard, LoNotice, LoStatus, LoTooltip],
	template: `
		<lo-card [heading]="model.name" [icon]="model.icon" [headingLevel]="2">
			@if (model.canShowInstructions) {
			<button
				lo-button
				variant="secondary"
				size="sm"
				type="button"
				loCardActions
				[disabled]="busy"
				(click)="instructions.emit()"
			>
				{{ model.instructionsLabel }}
			</button>
			} @if (model.canConnect) {
			<button
				lo-button
				size="sm"
				type="button"
				loCardActions
				[disabled]="busy"
				(click)="connect.emit()"
			>
				{{ model.connectLabel }}
			</button>
			} @if (model.canRevoke) {
			<button
				lo-button
				variant="secondary"
				size="sm"
				type="button"
				loCardActions
				[disabled]="busy"
				(click)="revoke.emit()"
			>
				Disconnect
			</button>
			}

			<div class="summary">
				<lo-status
					dot
					[tone]="
						model.state === 'connected'
							? 'success'
							: model.state === 'broken'
							? 'danger'
							: 'neutral'
					"
				>
					{{ model.statusLabel }}
				</lo-status>
				<p class="lo-card__text">{{ model.description }}</p>
			</div>

			@if (model.hasDetails) { @if (model.workspaceLabel) {
			<div class="workspace">
				<span class="lo-type-text-md">{{ model.workspaceLabel }}</span>
				@if (model.workspaceReference) {
				<span
					class="workspace__reference lo-type-text-xs"
					loTooltip="The workspace identifier this platform gave us. Quote it when asking support about a connection."
				>
					{{ model.workspaceReference }}
				</span>
				}
			</div>
			} @if (model.problem) {
			<lo-notice tone="warning" [heading]="model.problem"></lo-notice>
			} }
		</lo-card>
	`,
	styles: [
		`
			:host {
				display: block;

				.summary {
					display: flex;
					flex-wrap: wrap;
					align-items: center;
					gap: var(--lo-space-gap-sm) var(--lo-space-gap-md);
				}

				.workspace {
					display: flex;
					align-items: baseline;
					gap: var(--lo-space-gap-sm);
					min-width: 0;

					.workspace__reference {
						color: var(--lo-color-text-secondary);
					}
				}
			}
		`
	]
})
export class ChatChannelCardComponent {
	@Input() model: ChatChannelCardModel;

	/** True while this platform has a request in flight — a start, or a revoke.
	 *  Disables every action on the card rather than only the one pressed, so a
	 *  brand cannot revoke while an install is being started. */
	@Input() busy = false;

	@Output() connect = new EventEmitter<void>();
	@Output() revoke = new EventEmitter<void>();
	@Output() instructions = new EventEmitter<void>();
}
